import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  catchError,
  EMPTY,
  forkJoin,
  map,
  merge,
  Observable,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { BadgeComponent } from '../../../../../../ui-components/lib/badge/badge.component';
import { ButtonComponent } from '../../../../../../ui-components/lib/button/button.component';
import { ModalServiceService } from '../../../../../../ui-components/lib/modal/modal-service.service';
import { ToasterService } from '../../../../../../ui-components/services/toast/toast.service';
import { CustomWorkflowDrawerComponent } from '../../custom-workflow/custom-workflow-drawer/custom-workflow-drawer.component';
import { localized } from '../../custom-workflow/custom-workflow.model';
import { ReflectionScopeEnum } from '../enums/reflection-scope.enum';
import { ReflectionEditorComponent } from '../reflection-editor/reflection-editor.component';
import { ReflectionRunHistoryComponent } from '../reflection-run-history/reflection-run-history.component';
import {
  ConditionRule,
  PredefinedReflection,
  Reflection,
  ReflectionCatalog,
  ReflectionField,
  ReflectionList,
  ReflectionLookups,
} from '../reflections.models';
import { ReflectionsService } from '../reflections.service';
import { ReflectionPanelKindEnum } from './reflection-panel-kind.enum';

enum LoadMode {
  /** Catalog, fields and list. */
  FULL = 'full',
  /** The list alone. */
  LIST = 'list',
}

/** What the drawer shows: the editor (new when `reflection` is `null`) or the run history of one reflection. */
export interface ReflectionPanel {
  kind: ReflectionPanelKindEnum;
  reflection: Reflection | null;
}

/** A dynamic reflection with what the template needs to render its card. */
export interface ReflectionCardView {
  id: number;
  reflection: Reflection;
  name: string;
  actionLabel: string;
  configSummary: string | null;
  /** "Conditional", or `null` when the reflection has no conditions. */
  conditionBadge: string | null;
  /** The one-line "Only if amount > 50000", or `null` when the reflection has no conditions. */
  conditionSummary: string | null;
  /** The conditions alone, "amount > 50000". */
  conditionText: string | null;
  invalid: boolean;
  invalidReasons: string[];
  isActive: boolean;
}

/** A predefined (coded) reflection with what the template needs to render it. */
export interface PredefinedCardView {
  name: string;
  actionLabel: string | null;
  configSummary: string | null;
  unavailable: boolean;
}

export interface ReflectionGroupView {
  event: string;
  label: string;
  predefined: PredefinedCardView[];
  dynamic: ReflectionCardView[];
}

/** The drawer, with a key that changes whenever another reflection is opened, so the editor is recreated. */
interface PanelView {
  key: string;
  kind: ReflectionPanelKindEnum;
  reflection: Reflection | null;
  title: string;
}

/**
 * The reflections of one scope (flow, step or form) of a flow template, grouped by event: the predefined (coded)
 * ones read-only, then the dynamic ones as cards that can be reordered, switched on and off, edited, deleted and
 * inspected. The editor and the run history open in a drawer.
 *
 * It loads on init and again whenever `flowTemplateId`, `scope` or `stepTemplateId` change (closing the drawer);
 * the retry of a failed load runs the whole load again.
 */
@Component({
  selector: 'lib-reflection-list',
  imports: [
    TranslocoPipe,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    BadgeComponent,
    ButtonComponent,
    CustomWorkflowDrawerComponent,
    ReflectionEditorComponent,
    ReflectionRunHistoryComponent,
  ],
  templateUrl: './reflection-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReflectionListComponent {
  readonly service = inject(ReflectionsService);
  private readonly transloco = inject(TranslocoService);
  private readonly modalService = inject(ModalServiceService);
  private readonly toaster = inject(ToasterService);
  private readonly destroyRef = inject(DestroyRef);

  readonly deleteTemplate = viewChild<TemplateRef<unknown>>('confirmDelete');

  flowTemplateId = input.required<number>();
  scope = input.required<ReflectionScopeEnum>();
  stepTemplateId = input<number | null>(null);
  /** Hides add, edit, delete, the toggle and the drag handle; the history stays. */
  readonly = input(false);
  changed = output<void>();

  readonly PanelKind = ReflectionPanelKindEnum;

  private readonly list = signal<ReflectionList>({
    predefined: [],
    dynamic: [],
  });
  readonly fields = signal<ReflectionField[]>([]);
  readonly loading = signal(true);
  readonly loadFailed = signal(false);
  /** A reorder, toggle or delete is in flight: further changes wait for it. */
  readonly busy = signal(false);
  readonly panel = signal<ReflectionPanel | null>(null);
  private readonly pendingDelete = signal<Reflection | null>(null);
  private readonly attempt = signal(0);
  private readonly listReload$ = new Subject<void>();

  private readonly lang = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang(),
  });

  readonly groups = computed<ReflectionGroupView[]>(() => {
    const lang = this.lang();
    const catalog = this.service.catalog();
    const lookups = this.service.lookups();
    const { predefined, dynamic } = this.list();
    const fields = new Map(this.fields().map(field => [field.key, field]));
    const events = lookups?.events[this.scope()] ?? [];
    const known = new Set(events.map(event => event.value));
    // An entry of an event the lookup does not list must not vanish: it follows the known events.
    const unknown = [...predefined, ...dynamic]
      .map(entry => entry.event)
      .filter(
        (event, index, all) => !known.has(event) && all.indexOf(event) === index
      )
      .map(event => ({ value: event, label: event }));

    return [...events, ...unknown]
      .map(event => ({
        event: event.value,
        label: event.label,
        predefined: predefined
          .filter(entry => entry.event === event.value)
          .map(entry => this.predefinedView(entry, catalog, lang)),
        dynamic: dynamic
          .filter(entry => entry.event === event.value)
          .sort((a, b) => a.position - b.position)
          .map(entry => this.cardView(entry, catalog, lookups, fields, lang)),
      }))
      .filter(group => group.predefined.length || group.dynamic.length);
  });

  readonly panelView = computed<PanelView[]>(() => {
    const panel = this.panel();
    if (!panel) return [];
    const kind = panel.kind;
    const reflection = panel.reflection;
    const title =
      kind === ReflectionPanelKindEnum.HISTORY
        ? 'reflection_drawer_history_title'
        : reflection
          ? 'reflection_drawer_edit_title'
          : 'reflection_drawer_new_title';
    return [
      {
        key: `${kind}-${reflection?.id ?? 'new'}`,
        kind,
        reflection,
        title: this.transloco.translate(title),
      },
    ];
  });

  /** Loads again whenever the flow, the scope or the step changes (and on retry). */
  private readonly loadKey = computed(() => ({
    flowTemplateId: this.flowTemplateId(),
    scope: this.scope(),
    stepTemplateId: this.stepTemplateId(),
    attempt: this.attempt(),
  }));

  constructor() {
    const full$ = toObservable(this.loadKey).pipe(
      map((): LoadMode => LoadMode.FULL)
    );
    const list$ = this.listReload$.pipe(map((): LoadMode => LoadMode.LIST));
    // One loader: a newer load cancels the one before it, so a stale answer never overwrites the list.
    merge(full$, list$)
      .pipe(
        switchMap(mode => this.load(mode)),
        takeUntilDestroyed()
      )
      .subscribe();
  }

  /** Reloads the list only (after a change, or to recover from a failed one). */
  reload(): void {
    this.listReload$.next();
  }

  /** Runs the whole initial load again (catalog, fields and list). */
  retry(): void {
    this.attempt.update(attempt => attempt + 1);
  }

  add(): void {
    this.panel.set({ kind: ReflectionPanelKindEnum.EDITOR, reflection: null });
  }

  edit(reflection: Reflection): void {
    this.panel.set({ kind: ReflectionPanelKindEnum.EDITOR, reflection });
  }

  showHistory(reflection: Reflection): void {
    this.panel.set({ kind: ReflectionPanelKindEnum.HISTORY, reflection });
  }

  closePanel(): void {
    this.panel.set(null);
  }

  onSaved(): void {
    this.panel.set(null);
    this.reload();
    this.changed.emit();
  }

  drop(
    event: Pick<CdkDragDrop<unknown>, 'previousIndex' | 'currentIndex'>,
    group: ReflectionGroupView
  ): void {
    if (this.busy() || event.previousIndex === event.currentIndex) return;
    const ids = group.dynamic.map(card => card.id);
    moveItemInArray(ids, event.previousIndex, event.currentIndex);
    this.applyOrder(group.event, ids);
    this.busy.set(true);
    this.service
      .reorder(this.flowTemplateId(), {
        scope: this.scope(),
        step_template_id: this.stepTemplateId(),
        event: group.event,
        ids,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.finishMutation(),
        error: () => this.failMutation(),
      });
  }

  toggleActive(reflection: Reflection): void {
    if (this.busy()) return;
    this.busy.set(true);
    this.service
      .update(this.flowTemplateId(), reflection.id, {
        is_active: !reflection.is_active,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.finishMutation(),
        error: () => this.failMutation(),
      });
  }

  /** Asks for confirmation; {@link confirmRemove} deletes. */
  remove(reflection: Reflection): void {
    this.pendingDelete.set(reflection);
    this.modalService.modalContent.set({
      isOpen: true,
      hasCloseIcon: false,
      title: '',
      customizeContent: this.deleteTemplate(),
    });
  }

  cancelRemove(): void {
    this.pendingDelete.set(null);
    this.modalService.modalContent.set({ isOpen: false });
  }

  confirmRemove(): void {
    const reflection = this.pendingDelete();
    this.cancelRemove();
    if (!reflection || this.busy()) return;
    this.busy.set(true);
    this.service
      .remove(this.flowTemplateId(), reflection.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.finishMutation(),
        error: () => this.failMutation(),
      });
  }

  private load(mode: LoadMode): Observable<unknown> {
    const flowTemplateId = this.flowTemplateId();
    const list$ = this.service.list(
      flowTemplateId,
      this.scope(),
      this.stepTemplateId()
    );
    if (mode === LoadMode.LIST) {
      return list$.pipe(
        tap(list => this.showList(list.data)),
        catchError(() => this.showFailure())
      );
    }
    // Another flow / step: what the list shows (and the open panel) belongs to the previous one.
    this.panel.set(null);
    this.list.set({ predefined: [], dynamic: [] });
    this.fields.set([]);
    this.loadFailed.set(false);
    this.loading.set(true);
    return forkJoin([
      this.service.loadCatalog(),
      this.service.loadLookups(),
      this.service.fields(flowTemplateId),
      list$,
    ]).pipe(
      tap(([, , fields, list]) => {
        this.fields.set(fields.data);
        this.showList(list.data);
      }),
      catchError(() => this.showFailure())
    );
  }

  /** The load that follows a change is what ends it: `busy` stays set until then. */
  private showList(list: ReflectionList): void {
    this.list.set(list);
    this.loadFailed.set(false);
    this.loading.set(false);
    this.busy.set(false);
  }

  private showFailure(): Observable<never> {
    this.loadFailed.set(true);
    this.loading.set(false);
    this.busy.set(false);
    return EMPTY;
  }

  /** Shows the new order at once; the reload that follows (or the one after a failure) is the truth. */
  private applyOrder(event: string, ids: number[]): void {
    this.list.update(list => ({
      ...list,
      dynamic: list.dynamic.map(entry =>
        entry.event === event
          ? { ...entry, position: ids.indexOf(entry.id) + 1 }
          : entry
      ),
    }));
  }

  private finishMutation(): void {
    this.reload();
    this.changed.emit();
  }

  private failMutation(): void {
    this.toaster.add(
      this.transloco.translate('reflection_list_error_toast'),
      '',
      '',
      'error',
      true,
      true
    );
    this.reload();
  }

  private cardView(
    reflection: Reflection,
    catalog: ReflectionCatalog | null,
    lookups: ReflectionLookups | null,
    fields: Map<string, ReflectionField>,
    lang: string
  ): ReflectionCardView {
    const hook = catalog?.hooks.find(item => item.key === reflection.hook_key);
    const actionLabel = hook
      ? localized(hook.label, lang)
      : reflection.hook_key;
    const rules = reflection.conditions?.rules ?? [];
    const conditionText = rules.length
      ? rules
          .map(rule => this.ruleText(rule, lookups, fields, lang))
          .join(
            ` ${this.transloco.translate(
              reflection.conditions.match === 'any'
                ? 'reflection_condition_join_any'
                : 'reflection_condition_join_all'
            )} `
          )
      : null;
    return {
      id: reflection.id,
      reflection,
      name: reflection.name || actionLabel,
      actionLabel,
      configSummary: reflection.config_summary,
      conditionBadge: conditionText
        ? this.transloco.translate('reflection_conditional_badge')
        : null,
      conditionSummary: conditionText
        ? this.transloco.translate('reflection_conditional_summary', {
            summary: conditionText,
          })
        : null,
      conditionText,
      invalid: reflection.invalid,
      invalidReasons: reflection.invalid_reasons ?? [],
      isActive: reflection.is_active,
    };
  }

  private predefinedView(
    entry: PredefinedReflection,
    catalog: ReflectionCatalog | null,
    lang: string
  ): PredefinedCardView {
    const hook = entry.hook_key
      ? catalog?.hooks.find(item => item.key === entry.hook_key)
      : undefined;
    return {
      name: localized(entry.name, lang),
      actionLabel: hook ? localized(hook.label, lang) : null,
      configSummary: entry.config_summary,
      unavailable: entry.unavailable,
    };
  }

  /** "Amount > 50000": the field label, the operator label of the field's type and the value. */
  private ruleText(
    rule: ConditionRule,
    lookups: ReflectionLookups | null,
    fields: Map<string, ReflectionField>,
    lang: string
  ): string {
    const field = fields.get(rule.field_key);
    const operator = field
      ? lookups?.operators[field.type]?.find(
          item => item.value === rule.operator
        )
      : undefined;
    const value = this.valueText(rule.value, field, lang);
    return [
      field ? localized(field.label, lang) : rule.field_key,
      operator ? operator.label : rule.operator,
      value,
    ]
      .filter(part => part !== '')
      .join(' ');
  }

  private valueText(
    value: unknown,
    field: ReflectionField | undefined,
    lang: string
  ): string {
    if (value === undefined || value === null) return '';
    if (Array.isArray(value)) {
      return value.map(item => this.valueText(item, field, lang)).join(', ');
    }
    const option = field?.options.find(item => item.value === String(value));
    return option?.label ? localized(option.label, lang) : String(value);
  }
}
